import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import type React from "react";
import { useEffect, useRef } from "react";
import { RouterProvider } from "react-router-dom";
import { useApplicationInformationQuery } from "web_myplann/Api/Miscellaneous/useApplicationInformationQuery";
import { useInitializeWeb } from "web_myplann/Infrastructure/Hooks/useInitializeWeb";
import { useKeyboardScrollGuard } from "web_myplann/Infrastructure/Hooks/useKeyboardScrollGuard";
import { getRouter } from "web_myplann/Infrastructure/Routing/Router";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import { useColorScheme } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import UpdateVersionDialog from "web_myplann/Infrastructure/UpdateVersionDialog";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";
import { isWebViewMessage } from "web_myplann/Utils/isWebViewMessage";
import { handleIncomingMessage } from "web_myplann/Utils/WebViewMessageHandler";

const AppInner: React.FunctionComponent = () => {
	const { setMode } = useColorScheme();
	const { isAuthenticated } = useAtomValue(sessionState);
	const routerRef = useRef(getRouter());
	const queryClient = useQueryClient();

	useInitializeWeb();
	useKeyboardScrollGuard();

	const { data: applicationInformation } =
		useApplicationInformationQuery(isAuthenticated);

	useEffect(() => {
		if (applicationInformation?.activeColor != null) {
			setMode({ hspColor: applicationInformation.activeColor });
		}
	}, [applicationInformation, setMode]);

	useEffect(() => {
		const handleMessage = (nativeEvent: MessageEvent) => {
			if (!isWebViewMessage(nativeEvent.data)) {
				return;
			}

			const messageType = nativeEvent.data.type;
			void handleIncomingMessage(
				nativeEvent.data,
				setMode,
				queryClient,
				(path: string) => routerRef.current.navigate(path),
				() => routerRef.current.state.location.pathname,
			).catch((error: unknown) => {
				logWarning("Bridge message handling failed", {
					message_type: messageType,
					error: String(error),
				});
			});
		};

		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [setMode, queryClient]);

	return (
		<>
			<RouterProvider router={routerRef.current} />
			<UpdateVersionDialog />
		</>
	);
};

export default AppInner;
